import type {
  IterationUpdatedEvent,
  PartialObjective,
  Shape as ApiShape,
  StatusDistribution,
  StickyTree,
} from "@/backend/natsstubs/board/models";
import type {
  Link as ApiLink,
  SettingUpdatedEvent,
  TimerEvent as ApiTimerEvent,
} from "@/backend/natsstubs/session/models";
import type {
  StepEstimate,
  StickyType as ApiStickyType,
} from "@/backend/natsstubs/sticky_type/models";
import { statusClasses } from "@/model/baseTypes";
import type { StatusClass } from "@/model/baseTypes";
import type { BoardIteration } from "@/model/board";
import type { RelativeCoordinate } from "@/model/coordinates";
import type { Link } from "@/model/link";
import type { ObjectiveStickyTree, ObjectiveUpdate } from "@/model/objective";
import type { Shape } from "@/model/shape";
import type { StatusClassDistribution } from "@/model/status";
import type { StickyType } from "@/model/stickyType";
import type { ServerTime, TimerEvent } from "@/model/timer";
import type { Settings } from "@/store/serverSettings";
import { entriesIn, valueIn } from "@/utils/record";

export function mapApiLink(event: ApiLink): Link {
  return { id: event.id, from: event.fromStickyId, to: event.toStickyId };
}

export function mapApiShape(shape: ApiShape): Shape {
  return {
    ...shape,
    p0: shape.p0 as RelativeCoordinate,
    p1: shape.p1 as RelativeCoordinate,
    label: shape.label ?? undefined,
  };
}

export function mapApiStickyType(type: ApiStickyType): StickyType {
  return {
    ...type,
    altColor: type.altColor ?? type.color,
    almType: type.almType ?? null,
    origin: type.originBoardType,
    usable: type.availableBoardTypes,
    flexOrigin: type.originFlexboardType ?? null,
    flexUsable: type.availableFlexboardTypes ?? [],
    priorities: type.stepEstimate ? prioritiesByValue(type.stepEstimateValues) : undefined,
  };
}

function prioritiesByValue(stepEstimates?: StepEstimate[]) {
  if (stepEstimates) {
    return Object.fromEntries(
      stepEstimates.map((stepEstimate) => [stepEstimate.value, stepEstimate]),
    );
  }
}

export function mapApiIterationUpdate(
  event: IterationUpdatedEvent,
  existing: BoardIteration,
): BoardIteration {
  const statusDistribution = event.statusDistribution
    ? mapApiStatusDistribution(event.statusDistribution)
    : undefined;

  const velocity = parseFloat("" + event.velocity);
  const updateVelocity = Number.isFinite(velocity) ? velocity : undefined;

  return {
    ...existing,
    ...(updateVelocity !== undefined && { velocity: updateVelocity }),
    ...(statusDistribution && { ...totals(statusDistribution), statusDistribution }),
  };
}

export function mapApiStatusDistribution(
  distribution: StatusDistribution,
): StatusClassDistribution {
  return new Map(
    entriesIn(distribution)
      // Sort classes from undefined -> done
      .sort(
        ([s1], [s2]) =>
          statusClasses.indexOf(s1 as StatusClass) - statusClasses.indexOf(s2 as StatusClass),
      )
      .map(([status, data]) => [status as StatusClass, { points: data.points, value: data.value }]),
  );
}

function totals(statusDistribution: StatusClassDistribution) {
  // statusDistribution.values() directly is not working on bloody safari 17
  return [...statusDistribution.values()].reduce(
    (sum, value) => ({ items: sum.items + value.value, load: sum.load + value.points }),
    { items: 0, load: 0 },
  );
}

export function mapApiEvents(events: ApiTimerEvent[]): TimerEvent[] {
  return events.map((event) => ({
    ...event,
    type: "timer",
    boardId: event.boardId ?? undefined,
    artId: event.artId ?? undefined,
    createdBy: undefined,
    createdById: event.createdBy,
    updatedById: event.updatedBy,
    data: {
      ...event.data,
      end: event.data.end as ServerTime,
    },
  }));
}

type SettingValue = boolean | number | null;
type Mapper = (value?: SettingValue) => SettingValue;

const settingsMappings = (() => {
  return {
    isBacklogAlmStickyDeletable: [not, "backlogForbidJIRAStickyDelete"],
    isTeamAlmStickyDeletable: [not, "teamForbidStickyDelete"],
    isPriorityEditable: [not, "backlogForbidWSJFEdit"],
    confirmDelete: [orFalse, "confirmStickyDelete"],
    moveBetweenTeams: [orFalse, "moveActivated"],
    mirrorAssignedBacklogStickiesToTeamBoard: [orFalse, "backlogMirrorAssignedStickiesToTeamBoard"],
    iterationLoadWarn: [orNull, "iterationLoadWarn"],
    iterationLoadCritical: [orNull, "iterationLoadCritical"],
    showDevLinks: [orFalse, "showDevLinks"],
  } as const satisfies Record<string, [Mapper, keyof SettingUpdatedEvent]>;

  function not(value?: SettingValue) {
    return !value;
  }

  function orFalse(value?: SettingValue) {
    return value || false;
  }

  function orNull(value?: SettingValue) {
    return value ?? null;
  }
})();

export function mapApiSettings(event: SettingUpdatedEvent): Partial<Settings> {
  const settings: Record<string, SettingValue> = {};
  entriesIn(settingsMappings).forEach(([key, [mapper, prop]]) => {
    if (prop in event) {
      settings[key] = mapper(valueIn(event, prop));
    }
  });
  return settings;
}

export function mapApiObjective(obj: PartialObjective): Partial<ObjectiveUpdate> {
  return {
    ...obj,
    cards: obj.stickies,
    cardTree: mapCardTree(obj.stickyTree),
  };
}

function mapCardTree(tree?: StickyTree): ObjectiveStickyTree | undefined {
  if (tree) {
    return {
      cards: tree.stickies,
      statusDistribution: mapApiStatusDistribution(tree.statusDistribution),
      statusDistributionState: tree.statusDistributionState,
    };
  }
}
